import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Download, FileSpreadsheet, RotateCcw } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { Dayjs } from 'dayjs'
import { Navigate } from 'react-router-dom'
import { PageContainer } from '@/components/common/PageContainer'
import { PageHeader } from '@/components/common/PageHeader'
import { SectionCard } from '@/components/common/SectionCard'
import { PageEmpty, PageError, PageLoading } from '@/components/common/PageStates'
import {
  validationReportService,
  type ReportSelectOption,
  type ValidationReportFilters,
  type ValidationReportRow,
  type ValidationStatus,
} from '@/services/validationReport.service'
import { useRegionStore } from '@/stores/regionStore'
import { canAccessCustoutValidation } from '@/config/regions'

const { RangePicker } = DatePicker
const { Text } = Typography

type FormState = {
  locations: string[]
  range: [Dayjs, Dayjs] | null
  materialCode: string | undefined
  materialGroupCode: string | undefined
  materialGroupName: string | undefined
  document: string | undefined
  kitNo: string | undefined
}

const EMPTY: FormState = {
  locations: [],
  range: null,
  materialCode: undefined,
  materialGroupCode: undefined,
  materialGroupName: undefined,
  document: undefined,
  kitNo: undefined,
}

function toSelectOptions(
  values: Array<string | ReportSelectOption> | undefined
): ReportSelectOption[] {
  if (!values?.length) return []
  return values.map((v) => (typeof v === 'string' ? { value: v, label: v } : v))
}

function buildFilters(form: FormState): ValidationReportFilters {
  return {
    locations: form.locations.length ? form.locations : undefined,
    created_at_from: form.range?.[0]?.format('YYYY-MM-DD'),
    created_at_to: form.range?.[1]?.format('YYYY-MM-DD'),
    material_code: form.materialCode || undefined,
    material_group_code: form.materialGroupCode || undefined,
    material_group_name: form.materialGroupName || undefined,
    document: form.document || undefined,
    kit_no: form.kitNo || undefined,
  }
}

function hasAnyFilter(filters: ValidationReportFilters): boolean {
  return Object.values(filters).some((v) => {
    if (v == null) return false
    if (Array.isArray(v)) return v.length > 0
    return String(v).trim().length > 0
  })
}

function statusColor(status: ValidationStatus): string {
  switch (status) {
    case 'Match':
      return 'success'
    case 'No Match':
      return 'error'
    case 'Short Receipt':
      return 'warning'
    case 'Excess Received':
      return 'processing'
    default:
      return 'default'
  }
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  mode,
  loading,
}: {
  label: string
  value: string | string[] | undefined
  onChange: (v: any) => void
  options: ReportSelectOption[]
  placeholder: string
  mode?: 'multiple' | 'tags'
  loading?: boolean
}) {
  return (
    <Form.Item label={label}>
      <Select
        className="w-full"
        allowClear
        showSearch
        mode={mode}
        loading={loading}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        options={options}
        maxTagCount="responsive"
        filterOption={(input, option) => {
          if (!option) return false
          const label = String(option.label ?? '').toLowerCase()
          const val = String(option.value ?? '').toLowerCase()
          const needle = input.toLowerCase().trim()
          if (label.includes(needle) || val.includes(needle)) return true
          const unpaddedNeedle = needle.replace(/^0+/, '')
          if (unpaddedNeedle) {
            const unpaddedLabel = label.replace(/^0+/, '')
            const unpaddedVal = val.replace(/^0+/, '')
            if (unpaddedLabel.includes(unpaddedNeedle) || unpaddedVal.includes(unpaddedNeedle)) {
              return true
            }
          }
          return false
        }}
      />
    </Form.Item>
  )
}

export default function ValidationReportPage() {
  const [form, setForm] = useState<FormState>(EMPTY)
  const [applied, setApplied] = useState<ValidationReportFilters>({})
  const [downloading, setDownloading] = useState(false)
  const [lastFile, setLastFile] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const filters = useMemo(() => buildFilters(form), [form])
  const canDownload = hasAnyFilter(filters)
  const regionView = useRegionStore((s) => s.view)
  const availableRegions = useRegionStore((s) => s.availableRegions)
  const allowed = canAccessCustoutValidation(regionView, availableRegions)

  const optionsQ = useQuery({
    queryKey: ['rfid-validation-report-options', 'IN'],
    queryFn: () => validationReportService.getOptions(),
    staleTime: 5 * 60_000,
    enabled: allowed,
  })

  const previewQ = useQuery({
    queryKey: ['rfid-validation-report-preview', 'IN', applied],
    queryFn: () => validationReportService.preview(applied),
    enabled: allowed,
  })

  if (!allowed) {
    return <Navigate to="/dashboard" replace />
  }

  const patch = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setError(null)
  }

  const kitOptions = toSelectOptions(optionsQ.data?.kit_nos)
  const documentOptions = toSelectOptions(optionsQ.data?.documents)
  const materialOptions = optionsQ.data?.material_codes ?? []
  const materialGroupCodeOptions = optionsQ.data?.material_group_codes ?? []
  const materialGroupNameOptions = toSelectOptions(optionsQ.data?.material_group_names)

  const locationOptions = useMemo(() => {
    const map = new Map<string, string>()
    const add = (v?: string | ReportSelectOption) => {
      if (!v) return
      const val = typeof v === 'string' ? v.trim() : v.value?.trim()
      const lbl = typeof v === 'string' ? v.trim() : (v.label?.trim() || v.value?.trim())
      if (!val) return
      if (!map.has(val)) {
        map.set(val, lbl || val)
      } else if (lbl && lbl !== val && map.get(val) === val) {
        map.set(val, lbl)
      }
    }
    for (const l of optionsQ.data?.locations || []) add(l)
    for (const s of optionsQ.data?.senders || []) add(s)
    for (const r of optionsQ.data?.receivers || []) add(r)
    for (const c of optionsQ.data?.customer_locations || []) add(c)
    for (const w of optionsQ.data?.warehouses || []) add(w)
    for (const c2 of optionsQ.data?.c2_locations || []) add(c2)
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [optionsQ.data])

  const applyPreview = () => {
    if (Boolean(form.range?.[0]) !== Boolean(form.range?.[1])) {
      setError('Date range needs both start and end dates.')
      return
    }
    setError(null)
    setApplied({ ...filters })
  }

  const onDownload = async () => {
    if (!canDownload) {
      setError('Add at least one filter before downloading.')
      return
    }
    if (Boolean(form.range?.[0]) !== Boolean(form.range?.[1])) {
      setError('Date range needs both start and end dates.')
      return
    }

    setDownloading(true)
    setError(null)
    try {
      setApplied({ ...filters })
      const { filename } = await validationReportService.download(filters)
      setLastFile(filename)
      message.success(`Downloaded ${filename}`)
    } catch (e) {
      const msg = (e as { message?: string })?.message || 'Validation report download failed'
      setError(msg)
      message.error(msg)
    } finally {
      setDownloading(false)
    }
  }

  const optsLoading = optionsQ.isLoading
  const summary = previewQ.data?.summary
  const cell = (v: string | number | null | undefined) =>
    v == null || v === '' ? '—' : String(v)

  const columns: ColumnsType<ValidationReportRow> = [
    { title: 'RR no.', dataIndex: 'rr_no', key: 'rr_no', width: 120, fixed: 'left' },
    {
      title: 'Draft CUSTOUT (GEMS)',
      dataIndex: 'draft_tag',
      key: 'draft_tag',
      width: 180,
      fixed: 'left',
      render: (v: string | null) => cell(v),
    },
    {
      title: 'CUSTOUT (FC)',
      dataIndex: 'actual_tag',
      key: 'actual_tag',
      width: 180,
      fixed: 'left',
      onHeaderCell: () => ({ style: { background: '#fff566' } }),
      onCell: () => ({ style: { background: '#fffbe6' } }),
      render: (v: string | null) => cell(v),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      fixed: 'left',
      render: (status: ValidationStatus) => (
        <Tag color={statusColor(status)}>{status}</Tag>
      ),
    },
    { title: 'SAP Document', dataIndex: 'sap_document', key: 'sap_document', width: 140, render: cell },
    { title: 'id', dataIndex: 'id', key: 'id', width: 80, render: cell },
    { title: 'rfid_code', dataIndex: 'rfid_code', key: 'rfid_code', width: 180, render: cell },
    { title: 'warehouse', dataIndex: 'warehouse', key: 'warehouse', width: 120, render: cell },
    { title: 'plant_name', dataIndex: 'plant_name', key: 'plant_name', width: 160, render: cell },
    {
      title: 'customer_location',
      dataIndex: 'customer_location',
      key: 'customer_location',
      width: 140,
      render: cell,
    },
    {
      title: 'customer_name',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 160,
      render: cell,
    },
    { title: 'document', dataIndex: 'document', key: 'document', width: 140, render: cell },
    { title: 'In Transit', dataIndex: 'in_transit', key: 'in_transit', width: 100, render: cell },
    { title: 'is_virtual', dataIndex: 'is_virtual', key: 'is_virtual', width: 90, render: cell },
    { title: 'scan_status', dataIndex: 'scan_status', key: 'scan_status', width: 110, render: cell },
    { title: 'item_no', dataIndex: 'item_no', key: 'item_no', width: 90, render: cell },
    {
      title: 'transfer_type',
      dataIndex: 'transfer_type',
      key: 'transfer_type',
      width: 120,
      render: cell,
    },
    { title: 'remarks', dataIndex: 'remarks', key: 'remarks', width: 180, ellipsis: true, render: cell },
    { title: 'created_at', dataIndex: 'created_at', key: 'created_at', width: 110, render: cell },
    { title: 'updated_at', dataIndex: 'updated_at', key: 'updated_at', width: 110, render: cell },
    { title: 'created_by', dataIndex: 'created_by', key: 'created_by', width: 120, render: cell },
    { title: 'updated_by', dataIndex: 'updated_by', key: 'updated_by', width: 120, render: cell },
    { title: 'ref_doc_num', dataIndex: 'ref_doc_num', key: 'ref_doc_num', width: 140, render: cell },
    {
      title: 'material_code',
      dataIndex: 'material_code',
      key: 'material_code',
      width: 130,
      render: cell,
    },
    {
      title: 'material_desc',
      dataIndex: 'material_desc',
      key: 'material_desc',
      width: 180,
      ellipsis: true,
      render: cell,
    },
    { title: 'sender_code', dataIndex: 'sender_code', key: 'sender_code', width: 120, render: cell },
    {
      title: 'sender_name',
      dataIndex: 'sender_name',
      key: 'sender_name',
      width: 160,
      ellipsis: true,
      render: cell,
    },
    {
      title: 'receiver_code',
      dataIndex: 'receiver_code',
      key: 'receiver_code',
      width: 120,
      render: cell,
    },
    {
      title: 'receiver_name',
      dataIndex: 'receiver_name',
      key: 'receiver_name',
      width: 160,
      ellipsis: true,
      render: cell,
    },
    {
      title: 'material_group_code',
      dataIndex: 'material_group_code',
      key: 'material_group_code',
      width: 150,
      render: cell,
    },
    {
      title: 'material_group_name',
      dataIndex: 'material_group_name',
      key: 'material_group_name',
      width: 160,
      ellipsis: true,
      render: cell,
    },
    { title: 'kit_no', dataIndex: 'kit_no', key: 'kit_no', width: 110, render: cell },
  ]

  return (
    <PageContainer fluid>
      <PageHeader
        title="RFID Validation Report"
        subtitle="India only — compares draft CUSTOUT (GEMS) tags against posted SAP CUSTOUT (FC) per RR. Match, mismatch, short, and excess are flagged."
      />

      {error ? (
        <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />
      ) : null}

      {optionsQ.isError ? (
        <Alert
          type="warning"
          showIcon
          message="Could not load filter dropdowns from the server. You can still type filters after they load, or retry."
        />
      ) : null}

      {lastFile ? (
        <Alert
          type="success"
          showIcon
          icon={<FileSpreadsheet className="h-4 w-4" />}
          message={`Last download: ${lastFile}`}
        />
      ) : null}

      <SectionCard
        title="Filters"
        subtitle="Same location and party filters as RFID Reports. Defaults to CUSTOUT family. Document matches SAP doc or RR (ref_doc)."
      >
        <Form layout="vertical" className="report-filters">
          <Row gutter={[16, 8]}>
            <Col xs={24} lg={12}>
              <FilterSelect
                label="Location"
                mode="tags"
                loading={optsLoading}
                placeholder="Select or enter location, plant, customer, sender or receiver"
                value={form.locations}
                onChange={(v) => patch('locations', v ?? [])}
                options={locationOptions}
              />
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="Date range (created at)">
                <RangePicker
                  className="w-full"
                  value={form.range}
                  onChange={(v) => patch('range', (v as [Dayjs, Dayjs] | null) ?? null)}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12} xl={8}>
              <FilterSelect
                label="SKU (material code)"
                loading={optsLoading}
                placeholder="Select SKU"
                value={form.materialCode}
                onChange={(v) => patch('materialCode', v)}
                options={materialOptions}
              />
            </Col>
            <Col xs={24} md={12} xl={8}>
              <FilterSelect
                label="Material group code"
                loading={optsLoading}
                placeholder="Select material group code"
                value={form.materialGroupCode}
                onChange={(v) => patch('materialGroupCode', v)}
                options={materialGroupCodeOptions}
              />
            </Col>
            <Col xs={24} md={12} xl={8}>
              <FilterSelect
                label="Material group name"
                loading={optsLoading}
                placeholder="Select material group name"
                value={form.materialGroupName}
                onChange={(v) => patch('materialGroupName', v)}
                options={materialGroupNameOptions}
              />
            </Col>

            <Col xs={24} md={12} xl={8}>
              <FilterSelect
                label="Document / RR no."
                loading={optsLoading}
                placeholder="SAP document or RR (ref doc)"
                value={form.document}
                onChange={(v) => patch('document', v)}
                options={documentOptions}
              />
            </Col>
            <Col xs={24} md={12} xl={8}>
              <FilterSelect
                label="Kit number"
                loading={optsLoading}
                placeholder="Select kit number"
                value={form.kitNo}
                onChange={(v) => patch('kitNo', v)}
                options={kitOptions}
              />
            </Col>
          </Row>

          <div className="mt-2 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
            <Text type="secondary" className="text-xs">
              Preview works with or without filters. Download needs at least one filter.
            </Text>
            <Space wrap>
              <Button
                icon={<RotateCcw className="h-4 w-4" />}
                onClick={() => {
                  setForm(EMPTY)
                  setApplied({})
                  setError(null)
                  setLastFile(null)
                }}
              >
                Reset
              </Button>
              <Button onClick={applyPreview} loading={previewQ.isFetching}>
                Preview
              </Button>
              <Button
                type="primary"
                icon={<Download className="h-4 w-4" />}
                loading={downloading}
                disabled={!canDownload}
                onClick={() => void onDownload()}
              >
                Download Excel
              </Button>
            </Space>
          </div>
        </Form>
      </SectionCard>

      {previewQ.isLoading ? (
        <SectionCard title="Preview">
          <PageLoading />
        </SectionCard>
      ) : previewQ.isError ? (
        <PageError
          message={(previewQ.error as { message?: string })?.message || 'Preview failed'}
        />
      ) : (
        <SectionCard
          title="Preview"
          subtitle={`Preview of recent RRs — showing ${Math.min(previewQ.data?.data.length ?? 0, previewQ.data?.previewLimit ?? 50)} of ${previewQ.data?.total ?? 0} comparison rows. Download Excel for the full filtered set.`}
          count={previewQ.data?.total}
          variant="table"
        >
          {summary ? (
            <div className="mb-4 flex flex-wrap gap-2">
              <Tag color="success">Match: {summary.match}</Tag>
              <Tag color="error">No Match: {summary.no_match}</Tag>
              <Tag color="warning">Short Receipt: {summary.short_receipt}</Tag>
              <Tag color="processing">Excess Received: {summary.excess_received}</Tag>
            </div>
          ) : null}
          {(previewQ.data?.data.length ?? 0) === 0 ? (
            <PageEmpty description="No draft/posted CUSTOUT pairs match the current filters." />
          ) : (
            <Table
              size="small"
              rowKey={(r, i) =>
                `${r.region ?? ''}-${r.rr_no}-${r.draft_tag ?? ''}-${r.actual_tag ?? ''}-${r.status}-${i}`
              }
              columns={columns}
              dataSource={previewQ.data?.data ?? []}
              loading={previewQ.isFetching}
              pagination={false}
              scroll={{ x: true }}
            />
          )}
        </SectionCard>
      )}
    </PageContainer>
  )
}
